import { GetApp } from "../../App"
import { Block, BlockFieldInputsDivider } from "../../Components/Block"
import { INote, INoteType } from "../../Types"
import { TrackView } from "../TrackView"
import { TrackViewData } from "../TrackView/Layer"
import { TripViewData } from "../TripView"
import { GetData, GetPrintViewContext, TripListType, printableDays } from "./Context"
import { PrintTripsViewProps } from "./Params"
import { PrintCalculationsView } from "./CalculationsView"
import { PrintDayListView } from "./DayListView"
import { PrintDaysView } from "./DaysView"
import { PrintJournalView } from "./JournalView"
import { PrintNoteView } from "./NoteView"
import { PrintTechnicalView } from "./TechnicalView"

export function PrintTripsView({ trips }: PrintTripsViewProps) {
  const app = GetApp();
  const context = GetPrintViewContext();
  const data = GetData();
  const tc = app.getT("PrintView");

  if (!trips || trips.length === 0) {
    return null;
  }

  return (
    <div>
      {trips.map((trip) => {
        const { startLocation, endLocation } = TripViewData.tripLocations(trip);

        const tracks = data ? data.tracks.filter((track) => track.tripId == trip.id) : [];

        const tripDaysNotes = data ? printableDays(data.days).filter((d) => d.tripId == trip.id).flatMap((d) => d.notes || []) : [];
        const allTripNotes = [...(trip.notes || []), ...tripDaysNotes].filter((note, index, self) => self.findIndex((n) => n.id === note.id) === index);

        const notes = (trip.notes || []).filter((note) => note.tripId == trip.id && note.dayId == null && (note.title || note.note)).map((note: INote) => {
          return <PrintNoteView key={note.id} trip={trip} note={note} />;
        });

        const journalNotes = allTripNotes.filter((note) => note.tripId == trip.id && note.noteType === INoteType.journal && (note.title || note.note)).map((note: INote) => {
          const tripRef = note.tripId ? data?.trips.find((t) => t.id === note.tripId) : undefined;
          const day = note.dayId ? data?.days.find((d) => d.id === note.dayId) : undefined;
          return <PrintJournalView key={note.id} note={note} trip={tripRef} day={day} />;
        });

        const technicalNotes = allTripNotes.filter((note) => note.tripId == trip.id && note.noteType === INoteType.technical).map((note: INote) => {
          const tripRef = note.tripId ? data?.trips.find((t) => t.id === note.tripId) : undefined;
          const day = note.dayId ? data?.days.find((d) => d.id === note.dayId) : undefined;
          return <PrintTechnicalView key={note.id} note={note} trip={tripRef} day={day} />;
        });

        const tripDays = data ? printableDays(data.dayLists).filter((d) => d.tripId == trip.id) : [];
        const dayLists = tripDays.map((day) => <PrintDayListView key={day.id} day={day} />);

        const dayDetails = tripDays.map((day) => <PrintDaysView key={day.id} day={day} />);

        return (
          <>
            {context?.includeTripData && (
              <div>
                <Block className="PrintTripView container-boxed" key={trip.id}>
                  <h2>{tc("trip")}: {trip.title || "Untitled passage"}</h2>
                  <h4>
                    <span className="print-text-normal">{tc("from_date")}</span> {app.format("date", trip.startedAt)}
                    <span className="print-text-normal print-distance-left">{tc("until_date")}</span> {app.format("date", trip.completedAt)}
                    <span className="print-distance-left"><BlockFieldInputsDivider /></span>
                    <span className="print-text-normal print-distance-left">{tc("from_loc")}</span> {startLocation}
                    <span className="print-text-normal print-distance-left">{tc("to_loc")}</span> {endLocation}
                  </h4>
                  {TrackViewData.hasPositions(tracks) && (
                    <div className="PrintTripView-track">
                      <TrackView fullscreen={false} noInteraction={true} tracks={tracks} timezone={trip.timezone} />
                    </div>
                  )}

                  <div className="PrintTripView-statistics container-boxed-inner">
                    <h4>{tc("trip_statistics")}</h4>
                    {trip.calculated && <PrintCalculationsView calculated={trip.calculated} />}
                  </div>

                  {notes.length > 0 && (
                    <div className="PrintNoteView-trips container-boxed-inner">
                      {notes}
                    </div>
                  )}
                </Block>

                {context?.includeTripDayListType == TripListType.short && (
                  <Block className="PrintDayListView-block container-boxed">
                    <h3>{tc("day_list")}</h3>
                    {dayLists}
                  </Block>
                )}

                {context?.includeTripDayListType == TripListType.details && (
                  <Block className="PrintDaysView-block container-boxed">
                    <h3>{tc("day_list")}</h3>
                    {dayDetails}
                  </Block>
                )}

              </div>
            )}

            {context?.includeJournal && (
              <Block className="PrintJournalView-block container-boxed">
                <h3>{tc("list_journal")}</h3>
                {journalNotes}
              </Block>
            )}

            {context?.includeTechnical && (
              <Block className="PrintTechnicalView-block container-boxed">
                <h3>{tc("list_technical")}</h3>
                {technicalNotes}
              </Block>
            )}

          </>
        );
      })}
    </div>
  );
}
